import { get, post } from 'utils/apiWrapper';
import { prepareHeadersFromContext, DEFAULT_LIMIT } from 'utils/helpers';
import { COMPLETE_LOOK, SAVE_IMAGE, RETRIEVE_IMAGE } from 'utils/routes';

import { getDomainValue } from 'utils/domain';
import { $fetch } from 'utils/request';

type TSearchProductsUnbxd = {
  lang: string;
  market: string;
  query: string;
  filter?: string;
  sort?: string;
  page?: string;
  uid?: string;
  ipAddress?: string;
  translations?: string[];
};

type TUnbxdServiceResponse = {
  searchMetaData?: {
    status: number;
    queryTime: number;
    queryParams: {
      'log.response': string;
      'module.exclude': string;
      indent: string;
      'promotedSuggestion.sort': string;
      'alternate.op': string;
      'q.op': string;
      'popularProducts.fields': string;
      variants: string;
      'popularProducts.filter': Array<string>;
      'promotion.version': string;
      'req.rm.promotionEngine': string;
      'topQueries.sort': string;
      'inFields.count': string;
      'topQueries.count': string;
      'original.q': string;
      'popularProducts.count': string;
      'fallback.method': string;
      'req.rm.asterix': string;
      version: string;
      enableTaxonomy: string;
      q: string;
      'keywordSuggestions.count': string;
      'user.behaviour': string;
      'promotedSuggestion.count': string;
      fallback: string;
      enablePopularity: string;
      promotion: string;
    };
    fallback?: {
      q: string;
      original: {
        q: string;
        numberOfProducts: number;
      };
      reason: {
        msg: string;
        code: number;
      };
    };
  };
  response?: {
    numberOfProducts: number;
    start: number;
    products: {
      it_product_sheet_h1?: string;
      gender?: Array<string>;
      is_shooting_new?: string;
      color?: Array<string>;
      line?: string;
      brand?: Array<string>;
      age_group?: string;
      imageUrl?: Array<string>;
      it_target?: string;
      material?: string;
      IT_it_productUrl?: string;
      uniqueId: string;
      title?: string;
      IT_max_price_unx_d?: number;
      IT_currency_unx_ts?: string;
      autosuggest: string;
      doctype: string;
      result_count_timestamp_unx_l?: number;
      result_unbxd_double?: number;
      source_unbxd_fields?: Array<string>;
      carts_unbxd_double?: number;
      suggestion_length_unbxd_double?: number;
      orders_unbxd_double?: number;
      clicks_unbxd_double?: number;
      unbxdAutosuggestScore?: number;
      timeStamp_unbxd?: number;
      autosuggest_unstemmed?: string;
      unbxdFeedId?: string;
      hits_unbxd_double?: number;
      parent_unbxd?: boolean;
      all_doc_types_unx_tsm?: Array<string>;
      goods?: string;
      catalog?: string;
      grid_image_suffix?: string;
    }[];
  };
  redirect?: { type: string; value: string };
  facets?: {
    text?: {
      list?: {
        displayName: string;
        facetName: string;
        filterField: string;
        position: number;
        values: (string | number)[];
      }[];
    };
  };
};

export type TFacet = TUnbxdServiceResponse['facets'];

type TUnbxdResponse = {
  products: NonNullable<TUnbxdServiceResponse['response']>['products'];
  unbxd: boolean;
  facets?: TFacet;
  numberOfProducts: number;
  unbxdRequestId: string;
  fallback: {
    query: string;
    original: string;
  } | null;
  redirect: null | {
    type: string;
    value: string;
  };
};

export type TUnbxdProduct = TUnbxdResponse['products'][number];

const emptyResponse: TUnbxdResponse = {
  products: [],
  unbxd: false,
  numberOfProducts: 0,
  unbxdRequestId: '',
  fallback: null,
  redirect: null,
};

export async function searchProductsUnbxd({
  lang,
  market,
  query,
  filter = '',
  sort,
  page,
  uid,
  ipAddress,
  translations,
}: TSearchProductsUnbxd): Promise<TUnbxdResponse> {
  // new Promise<TUnbxdResponse | boolean>((resolve, reject) => {
  // translations tiene que existir y tener 2 valores diferentes de '' siempre
  // ya lo comprobamos para setear el booleano de searchNew pero por si acaso
  if (!translations || !Array.isArray(translations) || translations.length !== 2 || translations.some((item) => item === '')) {
    console.error('Missing required keys for unbxd API.');
    return emptyResponse;
  }
  const fields = [
    `${market}_currency_unx_ts`,
    `${market}_is_on_sale_unx_ts`,
    `${market}_max_price_EUR_unx_d`,
    `${market}_max_price_unx_d`,
    `${market}_min_price_EUR_unx_d`,
    `${market}_min_price_unx_d`,
    `${market}_max_sale_price_unx_d`,
    `${market}_min_sale_price_unx_d`,
    `${market}_max_sale_price_EUR_unx_d`,
    'age_group',
    'title',
    `${market}_${lang.toLowerCase()}_productUrl`,
    'material',
    'is_shooting_new',
    'color',
    'gender',
    `${lang.toLowerCase()}_target`,
    'grid_image_suffix',
    `${market}_voucher_prelaunch`,
    `${market}_voucher_code`,
    `${market}_voucher_type`,
    `${market}_voucher_campaign_type`,
    `${market}_voucher_autoapply`,
    `${market}_sale_percentage`,
    `${market}_voucher_discount_unx_ts`,
    `${market}_max_voucher_price_unx_ts`,
    `${market}_min_voucher_price_unx_ts`,
    'brand',
  ];
  const sizesFacet = `&facet.field=${market}_available_sizes_unx_tsm&f.${market}_available_sizes_unx_tsm.facet.sort=index&f.${market}_available_sizes_unx_tsm.facet.limit=50&f.${market}_available_sizes_unx_tsm.facet.display.name=Size&f.${market}_available_sizes_unx_tsm.facet.position=4`;
  const sortMap = {
    price_asc: 'asc',
    price_desc: 'desc',
  };
  let querySort = '';
  if (sort && sort.length && sort !== 'default') {
    querySort = `&sort=${market}_min_price_EUR_unx_d%20${sortMap[sort as keyof typeof sortMap]}`;
  }
  const baseApiKeyUnbxd = `/${translations.join('/')}/`;
  const queryString = `q=${encodeURIComponent(query)}&variants=false&facet.multiselect=false${sizesFacet}&filter=brand_uFilter:${getDomainValue({ camper: '(CAMPER OR CAMPERLAB)', nnormal: 'NNORMAL', camperlab: 'CAMPERLAB' })}&filter=available_in:${market}${filter}&fields=${fields.join(',')}&rows=${DEFAULT_LIMIT}&page=${page}${querySort}${uid ? `&uid=${uid}` : ''}&segment=domain%3A${getDomainValue(
    {
      camper: 'camper',
      nnormal: 'nnormal',
      camperlab: 'camperlab',
    },
  )}`;
  const rqHeaders: TODO = {
    'unbxd-user-id': uid,
  };
  if (ipAddress) {
    rqHeaders['X-forwarded-for'] = ipAddress;
  }
  try {
    const { data, headers } = await $fetch<TUnbxdServiceResponse>(
      { path: `${baseApiKeyUnbxd}search`, base: 'https://search.unbxd.io', queryString },
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...rqHeaders,
        } as TODO,
      },
      { responseHeaders: true },
    );

    const unbxdRequestId = headers ? headers.get('unx-request-id') || '' : '';
    const { response, facets, searchMetaData, redirect } = data;
    if (!response) {
      return emptyResponse;
    }
    const { products, numberOfProducts } = response;
    let fallback = null;
    if (searchMetaData?.fallback && Object.keys(searchMetaData.fallback).length > 0) {
      fallback = {
        query: searchMetaData.fallback.q,
        original: searchMetaData.fallback.original.q,
      };
    }
    return {
      products,
      unbxd: true,
      facets,
      numberOfProducts,
      unbxdRequestId,
      fallback,
      redirect: redirect ?? null,
    };
  } catch (e) {
    console.error(e);
    return emptyResponse;
  }
}

export async function autosuggestUnbxd(lang: string, market: string, query: string, translations: string[], uid: string): Promise<TUnbxdResponse | boolean> {
  // translations tiene que existir y tener 2 valores diferentes de '' siempre
  // ya lo comprobamos para setear el booleano de searchNew pero por si acaso
  try {
    if (!translations || !Array.isArray(translations) || translations.length !== 2 || translations.some((item) => item === '')) {
      console.error('Missing required keys for unbxd API.');
      return false;
    }

    const isBlankSearch = query === '*';
    const fields = [
      'title',
      'line',
      'imageUrl',
      'image_url',
      'salePrice',
      `${market}_${lang.toLowerCase()}_productUrl`,
      'price',
      `${market}_max_price_unx_d`,
      `${market}_max_sale_price_unx_d`,
      'currency',
      `${market}_currency_unx_ts`,
      `${lang.toLowerCase()}_product_sheet_h1`,
      'uniqueId',
      'id',
      'autosuggest',
      'doctype',
      'product_sheet_H1',
      `${market}_sale_percentage`,
      'age_group',
      'material',
      'is_shooting_new',
      'color',
      'gender',
      `${lang.toLowerCase()}_target`,
      `${market}_voucher_prelaunch`,
      `${market}_voucher_code`,
      `${market}_voucher_type`,
      `${market}_voucher_campaign_type`,
      `${market}_voucher_autoapply`,
      `${market}_voucher_discount_unx_ts`,
      `${market}_max_voucher_price_unx_ts`,
      `${market}_min_voucher_price_unx_ts`,
      'brand',
    ];

    const config: Record<string, any> = {
      version: 'V2',
      'inFields.count': 0,
      'popularProducts.count': 6,

      'topQueries.count': 10,
      'topQueries.sort': 'clicks_unbxd_double desc',

      'keywordSuggestions.count': isBlankSearch ? 0 : 2,
      'promotedSuggestion.count': isBlankSearch ? 5 : 3,
      'promotedSuggestion.sort': 'promoted_suggestion_priority_unbxd_double desc',

      'popularProducts.fields': fields.join(','),
      'popularProducts.filter': `available_in:"${market}"`,
      variants: false,
      fallback: true,
      'fallback.method': 'fuzzySearch',
      indent: 'off',
    };

    if (!isBlankSearch) {
      config['keywordSuggestions.sort'] = 'result_unbxd_double desc';
    }

    if (uid) {
      config.uid = uid;
    }

    const baseApiKeyUnbxd = `/${translations.join('/')}/`;
    const configString = new URLSearchParams(config).toString();
    const path = `${baseApiKeyUnbxd}autosuggest?q=${encodeURIComponent(query)}&${configString}&popularProducts.filter=brand_uFilter:${getDomainValue({ camper: '(CAMPER OR CAMPERLAB)', nnormal: 'NNORMAL', camperlab: 'CAMPERLAB' })}&segment=domain%3A${getDomainValue(
      {
        camper: 'camper',
        nnormal: 'nnormal',
        camperlab: 'camperlab',
      },
    )}`;

    const rqHeaders = {
      'unbxd-user-id': uid,
    };

    const result = await get({ path, base: 'https://search.unbxd.io', headers: rqHeaders as TODO, skipHeaders: true });
    const unbxdRequestId = result?.headers ? result?.headers['unx-request-id'] || '' : '';
    const { data } = result;
    const { response, facets, searchMetaData, redirect } = data;

    if (!response) {
      return false;
    }

    const { products, numberOfProducts } = response;
    let fallback = null;
    if (searchMetaData.fallback && Object.keys(searchMetaData.fallback).length > 0) {
      fallback = {
        query: searchMetaData.fallback.q,
        original: searchMetaData.fallback.original.q,
      };
    }
    return {
      products: products?.filter((item: TODO) => item.result_unbxd_double !== 0),
      unbxd: true,
      facets,
      numberOfProducts,
      unbxdRequestId,
      fallback,
      redirect: redirect ?? null,
    };
  } catch (e) {
    console.error(e);
    return false;
  }
}

export async function autosuggestUnbxdV2({
  lang,
  market,
  query,
  translations,
  uid,
}: {
  lang: string;
  market: string;
  query: string;
  translations: string[];
  uid: string | null;
}): Promise<TUnbxdResponse> {
  // translations tiene que existir y tener 2 valores diferentes de '' siempre
  // ya lo comprobamos para setear el booleano de searchNew pero por si acaso
  try {
    if (!translations || !Array.isArray(translations) || translations.length !== 2 || translations.some((item) => item === '')) {
      console.error('Missing required keys for unbxd API.');
      return emptyResponse;
    }

    const isBlankSearch = query === '*';
    const fields = [
      'title',
      'line',
      'imageUrl',
      'image_url',
      'salePrice',
      `${market}_${lang.toLowerCase()}_productUrl`,
      'price',
      `${market}_max_price_unx_d`,
      `${market}_max_sale_price_unx_d`,
      'currency',
      `${market}_currency_unx_ts`,
      `${lang.toLowerCase()}_product_sheet_h1`,
      'uniqueId',
      'id',
      'autosuggest',
      'doctype',
      'product_sheet_H1',
      `${market}_sale_percentage`,
      'age_group',
      'material',
      'is_shooting_new',
      'color',
      'gender',
      `${lang.toLowerCase()}_target`,
      `${market}_voucher_prelaunch`,
      `${market}_voucher_code`,
      `${market}_voucher_type`,
      `${market}_voucher_campaign_type`,
      `${market}_voucher_autoapply`,
      `${market}_voucher_discount_unx_ts`,
      `${market}_max_voucher_price_unx_ts`,
      `${market}_min_voucher_price_unx_ts`,
      'brand',
    ];

    const config: Record<string, any> = {
      version: 'V2',
      'inFields.count': 0,
      'popularProducts.count': 6,

      'topQueries.count': 10,
      'topQueries.sort': 'clicks_unbxd_double desc',

      'keywordSuggestions.count': isBlankSearch ? 0 : 2,
      'promotedSuggestion.count': isBlankSearch ? 5 : 3,
      'promotedSuggestion.sort': 'promoted_suggestion_priority_unbxd_double desc',

      'popularProducts.fields': fields.join(','),
      'popularProducts.filter': `available_in:"${market}"`,
      variants: false,
      fallback: true,
      'fallback.method': 'fuzzySearch',
      indent: 'off',
    };

    if (!isBlankSearch) {
      config['keywordSuggestions.sort'] = 'result_unbxd_double desc';
    }

    if (uid) {
      config.uid = uid;
    }

    const configString = new URLSearchParams(config).toString();
    const queryString = `q=${encodeURIComponent(query)}&${configString}&popularProducts.filter=brand_uFilter:${getDomainValue({ camper: '(CAMPER OR CAMPERLAB)', nnormal: 'NNORMAL', camperlab: 'CAMPERLAB' })}&segment=domain%3A${getDomainValue(
      {
        camper: 'camper',
        nnormal: 'nnormal',
        camperlab: 'camperlab',
      },
    )}`;

    const { data, headers } = await $fetch<TUnbxdServiceResponse>(
      {
        base: 'https://search.unbxd.io',
        path: `/${translations.join('/')}/autosuggest`,
        queryString,
      },
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'unbxd-user-id': uid,
        } as TODO,
      },
      { responseHeaders: true },
    );

    // const result = await get({ path, base: 'https://search.unbxd.io', headers: rqHeaders, skipHeaders: true });
    const unbxdRequestId = headers?.get('unx-request-id') ?? '';
    // const { data } = result;
    const { response, facets, searchMetaData, redirect } = data;
    if (!response) {
      return {
        ...emptyResponse,
      };
    }

    const { products, numberOfProducts } = response;
    let fallback = null;
    if (searchMetaData?.fallback && Object.keys(searchMetaData.fallback).length > 0) {
      fallback = {
        query: searchMetaData.fallback.q,
        original: searchMetaData.fallback.original.q,
      };
    }
    return {
      products: products?.filter((item) => item.result_unbxd_double !== 0),
      unbxd: true,
      facets,
      numberOfProducts,
      unbxdRequestId,
      fallback,
      redirect: redirect ?? null,
    };
  } catch (e) {
    console.error(e);
    return emptyResponse;
  }
}

export async function completeLookUnbxd({ profile, image }: { profile: string; image: string }) {
  try {
    const formData = new FormData();
    formData.append('profile', profile);
    formData.append('image', image);

    const completeLookResponse = await post({
      path: COMPLETE_LOOK,
      data: formData,
    });

    const { status, valid, query, errors } = completeLookResponse.data;

    if (status === 'OK') {
      return { valid, query };
    } else {
      return { valid: false, query, errors };
    }
  } catch (e) {
    console.error(e);
    return { valid: false, query: '' };
  }
}

export async function searchByImageUnbxd({
  lang,
  market,
  image,
  translations,
  filter = '',
  uid,
  sort,
}: {
  lang: string;
  market: string;
  image: string;
  translations: string[];
  filter?: string;
  uid?: string;
  sort?: string;
}) {
  try {
    if (!image) {
      console.error('Missing image, this should not happen.');
      return false;
    }

    const fields = [
      `${market}_currency_unx_ts`,
      `${market}_is_on_sale_unx_ts`,
      `${market}_max_price_EUR_unx_d`,
      `${market}_max_price_unx_d`,
      `${market}_min_price_EUR_unx_d`,
      `${market}_min_price_unx_d`,
      `${market}_max_sale_price_unx_d`,
      `${market}_min_sale_price_unx_d`,
      `${market}_max_sale_price_EUR_unx_d`,
      'age_group',
      'title',
      `${market}_${lang.toLowerCase()}_productUrl`,
      'material',
      'is_shooting_new',
      'color',
      'gender',
      `${lang.toLowerCase()}_target`,
      'grid_image_suffix',
      `${market}_voucher_prelaunch`,
      `${market}_voucher_code`,
      `${market}_voucher_type`,
      `${market}_voucher_campaign_type`,
      `${market}_voucher_autoapply`,
      `${market}_sale_percentage`,
      `${market}_voucher_discount_unx_ts`,
      `${market}_max_voucher_price_unx_ts`,
      `${market}_min_voucher_price_unx_ts`,
      'uniqueId',
      'imageUrl',
    ];
    const sortMap = {
      price_asc: 'asc',
      price_desc: 'desc',
    };

    let querySort = '';
    if (sort && sort.length && sort !== 'default') {
      querySort = `&sort=${market}_min_price_EUR_unx_d%20${sortMap[sort as keyof typeof sortMap]}`;
    }

    const siteKey = translations[1];
    const baseApiKeyUnbxd = `/${siteKey}/`;
    const path = `${baseApiKeyUnbxd}images?rows=${DEFAULT_LIMIT}&start=0&variants=false&facet.multiselect=false&facet.field=${market}_available_sizes_unx_tsm&f.${market}_available_sizes_unx_tsm.facet.sort=index&f.${market}_available_sizes_unx_tsm.facet.limit=50&f.${market}_available_sizes_unx_tsm.facet.display.name=Size&filter=available_in:${market}${filter}&fields=${fields.join(',')}&popularProducts.filter=brand_uFilter:${getDomainValue({ camper: '(CAMPER OR CAMPERLAB)', nnormal: 'NNORMAL', camperlab: 'CAMPERLAB' })}${querySort}&segment=domain%3A${getDomainValue(
      {
        camper: 'camper',
        nnormal: 'nnormal',
        camperlab: 'camperlab',
      },
    )}`;

    const [dataType, base64Image] = image.split(',');
    const [, contentType] = dataType?.split(':') ?? [];
    const rqHeaders: TODO = {
      Authorization: translations[0],
      'unbxd-user-id': uid,
      'Content-Type': contentType,
    };

    const payload = {
      variants: false,
      'facet.multiselect': true,
      'facet.field': `${market}_available_sizes_unx_tsm`,
      [`f.${market}_available_sizes_unx_tsm.facet.sort`]: 'index',
      [`f.${market}_available_sizes_unx_tsm.facet.limit`]: 50,
      [`f.${market}_available_sizes_unx_tsm.facet.display.name`]: 'Size',
      [`f.${market}_available_sizes_unx_tsm.facet.position`]: 4,
      filter: `available_in:${market}${filter}`,
      fields: fields.join(','),
      rows: DEFAULT_LIMIT,
    };

    if (uid) {
      payload.uid = uid;
    }

    const result = await post({ path, base: 'https://search.unbxd.io/v2.0/sites', headers: rqHeaders as TODO, data: base64Image, skipHeaders: true });

    const unbxdRequestId = result?.headers ? result?.headers['unx-request-id'] || '' : '';
    const { data } = result;
    const { response, facets, searchMetaData } = data;

    if (!response) {
      return false;
    }

    const { products, numberOfProducts } = response;
    let fallback = null;
    if (searchMetaData.fallback && Object.keys(searchMetaData.fallback).length > 0) {
      fallback = {
        query: searchMetaData.fallback.q,
        original: searchMetaData.fallback.original.q,
      };
    }

    return {
      products,
      facets: facets || {},
      numberOfProducts,
      unbxdRequestId,
      fallback,
    };
  } catch (e) {
    console.error(e);
    return false;
  }
}

export async function saveImageForSearch({ profile, encodedImage }: { profile: string; encodedImage: string }) {
  const result = await post({
    path: SAVE_IMAGE,
    data: {
      profile,
      encodedImage,
    },
  });

  const { data } = result;

  if (data.status === 'OK') {
    return data.id;
  }

  return false;
}

export async function retrieveImageForSearch({ profile, id, context }: { profile: string; id: string; context: TODO }) {
  const headers = prepareHeadersFromContext(context);
  const path = `${RETRIEVE_IMAGE}?profile=${profile}&id=${id}`;

  const result = await get({ path, headers: headers as TODO });
  const { data } = result;

  if (data.status === 'OK' && data.encodedImage) {
    return data.encodedImage;
  }

  return false;
}
